/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import * as _ from 'underscore';
import { Logger } from '@core/lib/services';
import { Injectable } from '@angular/core';
import { ReportContextInfo } from '@app/di/data-model/report-context-info';
import { ReportContextItem } from '@app/di/data-model/report-context-item';
import { UtilService } from '@app/util/util.service';
import { AuditService } from '@app/common/audit.service';

const log = new Logger('ReportContextService');

@Injectable({
  providedIn: 'root'
})
export class ReportContextService {
  private contextInfo = new ReportContextInfo();
  private nprContextInfo = {} as any;

  constructor(private auditService: AuditService) {}
  clearDxHubContext(contextId) {
    if (contextId === this.contextInfo.dxHubContext) {
      this.contextInfo.dxHubContext = '';
      this.contextInfo.previousRequestedProcId = '';
    }
  }

  setReportContext(accession) {
    this.contextInfo.reportContext = accession;
  }

  getReportContext() {
    return this.contextInfo.reportContext;
  }

  setReportLaunchTime(time) {
    this.contextInfo.reportLaunchTime = time;
  }

  getReportLaunchTime() {
    return this.contextInfo.reportLaunchTime;
  }

  getContextCorrespondingToAccessionNum(accession) {
    const contextItem = { accession };
    const context = _.findWhere(this.contextInfo.contextItemList, contextItem);
    return context !== undefined ? context.requestedProcId : '';
  }

  getStudyIdentifierOfGivenContext(requestedProcedureID) {
    const index = UtilService.findIndexById(requestedProcedureID, this.contextInfo.contextItemList);
    if (index > -1) {
      return this.contextInfo.contextItemList[index].accession;
    }
  }

  getCurrentAccession() {
    return this.contextInfo.currentIndex !== -1
      ? this.contextInfo.contextItemList[this.contextInfo.currentIndex].accession
      : '';
  }

  getCurrentRequestedProcId() {
    return this.contextInfo.currentIndex !== -1
      ? this.contextInfo.contextItemList[this.contextInfo.currentIndex].requestedProcId
      : '';
  }

  getSiteName() {
    return this.contextInfo.siteName;
  }

  getPreviousAccession() {
    const index = UtilService.findIndexById(this.contextInfo.previousRequestedProcId, this.contextInfo.contextItemList);
    return index !== -1 ? this.contextInfo.contextItemList[index].accession : '';
  }

  getPreviousRequestedProcId() {
    return this.contextInfo.previousRequestedProcId;
  }

  getActiveContext() {
    return this.contextInfo.activeContext;
  }

  getDiscardFlag() {
    return this.contextInfo.isDiscard;
  }

  removeContext(requestedProcId) {
    const studyIndex = UtilService.findIndexById(requestedProcId, this.contextInfo.contextItemList);
    if (studyIndex !== -1) {
      // Rearrange the context items list
      this.contextInfo.contextItemList.splice(studyIndex, 1);
      // Current index need to adjust based on the active tab index ( current index ).
      // On closing inactive tab we need to ensure that current context remains unchanged.
      // On closing active tab, current index will update on read action for the next active tab.
      if (studyIndex < this.contextInfo.currentIndex) {
        // If the removed index is less than the current index then rearrange the current index
        this.contextInfo.currentIndex = this.contextInfo.currentIndex > -1 ? this.contextInfo.currentIndex - 1 : -1;
      } else if (studyIndex === this.contextInfo.currentIndex) {
        // If the removed index and current index are same the reset the current index to -1
        this.contextInfo.currentIndex = -1;
      }
    }
  }

  setCurrentContext(requestedProcId, accession, siteName?) {
    this.contextInfo.siteName = siteName;
    // Check for exam which doesn't have report - preview/locked exam/no accession, etc
    if (accession === '') {
      if (requestedProcId !== this.contextInfo.dxHubContext) {
        // set previous context and reset currentIndex
        this.contextInfo.previousRequestedProcId =
          this.contextInfo.currentIndex !== -1
            ? this.contextInfo.contextItemList[this.contextInfo.currentIndex].requestedProcId
            : '';
      }
      this.contextInfo.currentIndex = -1;
      this.contextInfo.dxHubContext = requestedProcId;
      return;
    }

    if (requestedProcId !== this.contextInfo.dxHubContext) {
      // set previous context
      this.contextInfo.previousRequestedProcId =
        this.contextInfo.currentIndex !== -1
          ? this.contextInfo.contextItemList[this.contextInfo.currentIndex].requestedProcId
          : '';
    }

    // Add/select current context
    const index = UtilService.findIndexById(requestedProcId, this.contextInfo.contextItemList);
    const studyFound = index > -1;
    this.contextInfo.activeContext = accession;

    if (studyFound) {
      this.contextInfo.currentIndex = index;
    } else {
      this.contextInfo.contextItemList[this.contextInfo.contextItemList.length] = new ReportContextItem(
        accession,
        requestedProcId
      );
      this.contextInfo.currentIndex = this.contextInfo.contextItemList.length - 1;
    }
    this.contextInfo.dxHubContext = requestedProcId;
  }

  /**
   * Function to check whether report is in context
   *
   */
  checkReportInContext(studyid) {
    const contextItem = {};
    const ncdToSearch = 'requestedProcId';
    contextItem[ncdToSearch] = studyid;
    const studyIdentifier = _.findWhere(this.contextInfo.contextItemList, contextItem);
    return studyIdentifier !== undefined;
  }

  /**
   * Method to set discard flag to discard open report.
   */
  setDiscardFlag() {
    this.contextInfo.isDiscard = true;
    log.debug(' setDiscardFlag :' + this.contextInfo.isDiscard);
  }

  /**
   * Method to reset discard flag to discard open report.
   */
  resetDiscardFlag() {
    this.contextInfo.isDiscard = false;
    log.debug(' resetDiscardFlag :' + this.contextInfo.isDiscard);
  }

  /**
   * Function to get associated exam list
   *
   */
  getAssociatedExams(primaryContext) {
    let associatedExamList = [];
    const index = UtilService.findIndexById(primaryContext, this.contextInfo.contextItemList);
    if (index > -1) {
      associatedExamList = this.contextInfo.contextItemList[index].associatedExamList;
    }
    return associatedExamList;
  }

  getNPRContext() {
    return this.nprContextInfo;
  }

  setNPRContext(reportId, examId, accession) {
    this.nprContextInfo.reportId = reportId;
    this.nprContextInfo.examId = examId;
    this.nprContextInfo.accession = accession;
  }

  setNPRContextAuditData(target) {
    this.nprContextInfo.auditData = this.auditService.getExamAuditInfo(target);
  }

  getNPRContextAuditData() {
    return this.nprContextInfo.auditData;
  }

  clearNPRContext() {
    this.nprContextInfo = {};
  }

  getReportId() {
    return this.nprContextInfo.reportId;
  }
}
